import { yupResolver } from '@hookform/resolvers/yup'
import { find, map } from 'lodash-es'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import { Color, EmailTemplateColor, Gradient } from '../../../../common/models'
import FormGroup from '../../../../core/components/FormGroup'
import { useCheckPermission } from '../../../../core/hooks/use-check-permission'
import {
  getEmailTemplateColors,
  updateEmailTemplateColors,
} from '../../../../core/services/email-templates'
import { getSettingsColors } from '../../../../core/services/festival-settings'
import { ColorCircle } from '../../ColorCircle'
import { MessageBox } from '../../messages/MessageBox'
import { Button, Card } from '@/components/ui'
import SelectField from '@/components/select-field'
import type { SelectOption } from '@/components/select-field'
import Loader from '@/common/components/Loader'

interface IColorValues {
  bgColorName: string
  headerColorName: string
  textColorName: string
  textHighlightColorName: string
  heroCardBgColorName: string
  heroCardTextColorName: string
  heroCardErrorBgColorName: string
  heroCardErrorTextColorName: string
}

export const ColorSettings: React.FC = () => {
  const { editionId, festivalName } = useParams()
  const { hasEditPermission } = useCheckPermission({ page: 'SETTINGS' })
  const [emailColors, setEmailColors] = useState<EmailTemplateColor>()
  const [loading, setLoading] = useState<boolean>(false)

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const [gradients, setGradients] = useState<Gradient[]>()
  const [colors, setColors] = useState<Color[]>()

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        setLoading(true)
        const response = await getEmailTemplateColors()
        setEmailColors(response.data)
        const response2 = await getSettingsColors()
        const main = find(response2.data, data => data.name === 'PRIMARY')
        const secondary = find(response2.data, data => data.name === 'SECONDARY')
        if (secondary?.gradients && main?.gradients) {
          setGradients([...main?.gradients, ...secondary?.gradients])
        }
        if (main?.colors) {
          setColors(main.colors)
        }
        setTimeout(() => {
          setColorValue('bgColorName', response.data.bgColorName)
          setColorValue('headerColorName', response.data.headerColorName)
          setColorValue('textColorName', response.data.textColorName)
          setColorValue('textHighlightColorName', response.data.textHighlightColorName)
          setColorValue('heroCardBgColorName', response.data.heroCardBgColorName)
          setColorValue('heroCardTextColorName', response.data.heroCardTextColorName)
          setColorValue('heroCardErrorBgColorName', response.data.heroCardErrorBgColorName)
          setColorValue('heroCardErrorTextColorName', response.data.heroCardErrorTextColorName)
        }, 100)
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }, [editionId, festivalName])

  const colorSchema = yup.object().shape({
    bgColorName: yup.string().required('Field required!'),
    headerColorName: yup.string().required('Field required!'),
    textColorName: yup.string().required('Field required!'),
    textHighlightColorName: yup.string().required('Field required!'),
    heroCardBgColorName: yup.string().required('Field required!'),
    heroCardTextColorName: yup.string().required('Field required!'),
    heroCardErrorBgColorName: yup.string().required('Field required!'),
    heroCardErrorTextColorName: yup.string().required('Field required!'),
  })

  const {
    handleSubmit: handleSubmitColor,
    register: registerColor,
    formState: { errors: errorsColor },
    setValue: setColorValue,
    watch: colorWatch,
  } = useForm<IColorValues>({
    mode: 'onChange',
    resolver: yupResolver<IColorValues>(colorSchema),
  })

  const submitColor = async (values: IColorValues) => {
    setLoading(true)
    const data: EmailTemplateColor = {
      bgColorName: values.bgColorName,
      headerColorName: values.headerColorName,
      textColorName: values.textColorName,
      textHighlightColorName: values.textHighlightColorName,
      heroCardBgColorName: values.heroCardBgColorName,
      heroCardTextColorName: values.heroCardTextColorName,
      heroCardErrorBgColorName: values.heroCardErrorBgColorName,
      heroCardErrorTextColorName: values.heroCardErrorTextColorName,
    }
    try {
      const { status } = await updateEmailTemplateColors(data)
      if (status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Update Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  const bgColorNameWatch = colorWatch('bgColorName')
  const headerColorNameWatch = colorWatch('headerColorName')
  const textColorNameWatch = colorWatch('textColorName')
  const textHighlightColorNameWatch = colorWatch('textHighlightColorName')
  const heroCardBgColorNameWatch = colorWatch('heroCardBgColorName')
  const heroCardTextColorNameWatch = colorWatch('heroCardTextColorName')
  const heroCardErrorBgColorNameWatch = colorWatch('heroCardErrorBgColorName')
  const heroCardErrorTextColorNameWatch = colorWatch(
    'heroCardErrorTextColorName',
  )

  const colorOptions = useMemo<SelectOption[]>(
    () => [
      ...map(colors, (color) => ({
        label: <ColorCircle color1={color} color2={color} text={color.name} />,
        value: color.name,
        textValue: color.name,
      })),
      ...map(gradients, (gradient) => ({
        label: <ColorCircle gradient={gradient} text={gradient.name} />,
        value: gradient.name,
        textValue: gradient.name,
      })),
    ],
    [colors, gradients],
  )

  const handleCancelColor = async () => {
    if (emailColors) {
      setColorValue('bgColorName', emailColors.bgColorName)
      setColorValue('headerColorName', emailColors.headerColorName)
      setColorValue('textColorName', emailColors.textColorName)
      setColorValue(
        'textHighlightColorName',
        emailColors.textHighlightColorName,
      )
      setColorValue('heroCardBgColorName', emailColors.heroCardBgColorName)
      setColorValue('heroCardTextColorName', emailColors.heroCardTextColorName)
      setColorValue(
        'heroCardErrorBgColorName',
        emailColors.heroCardErrorBgColorName,
      )
      setColorValue(
        'heroCardErrorTextColorName',
        emailColors.heroCardErrorTextColorName,
      )
    }
  }

  return (
    <>
      {loading && <Loader />}
      <Card className="p-[30px] max-h-[calc(100vh-215px)] bg-gradient-4 overflow-y-auto">
        <div>
          <h4 className="text-title-xs text-monochrome-1 mb-4">
            Color Settings
          </h4>

          <form
            id="settings-email-color-form"
            onSubmit={handleSubmitColor(submitColor)}
          >
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-monochrome-3-60 border border-monochrome-120 p-4 rounded-2xl">
                <FormGroup
                  label="Header Color"
                  direction="column"
                  labelWrapperClassName="w-full justify-between"
                  labelClassName="whitespace-nowrap min-w-[230px]"
                >
                  <div className="flex flex-col gap-2 w-full">
                    <SelectField
                      outline
                      placeholder="Select a Color"
                      value={headerColorNameWatch}
                      onValueChange={(value) =>
                        setColorValue('headerColorName', value)
                      }
                      options={colorOptions}
                      disabled={!hasEditPermission}
                      error={errorsColor.headerColorName?.message}
                      name="headerColorName"
                      required
                    />
                  </div>
                </FormGroup>
              </div>
              <div className="bg-monochrome-3-60 border border-monochrome-120 p-4 rounded-2xl">
                <FormGroup
                  label="General Background Color"
                  direction="column"
                  labelWrapperClassName="w-full justify-between"
                  labelClassName="whitespace-nowrap min-w-[230px]"
                >
                  <div className="flex flex-col gap-2 w-full">
                    <SelectField
                      outline
                      placeholder="Select a Color"
                      value={bgColorNameWatch}
                      onValueChange={(value) =>
                        setColorValue('bgColorName', value)
                      }
                      options={colorOptions}
                      disabled={!hasEditPermission}
                      error={errorsColor.bgColorName?.message}
                      name="bgColorName"
                      required
                    />
                  </div>
                </FormGroup>
              </div>
              <div className="bg-monochrome-3-60 border border-monochrome-120 p-4 rounded-2xl">
                <FormGroup
                  label="Hero Card General Color"
                  direction="column"
                  labelWrapperClassName="w-full justify-between"
                  labelClassName="whitespace-nowrap min-w-[230px]"
                >
                  <div className="flex flex-col gap-2 w-full">
                    <SelectField
                      outline
                      placeholder="Select a Color"
                      value={heroCardBgColorNameWatch}
                      onValueChange={(value) =>
                        setColorValue('heroCardBgColorName', value)
                      }
                      options={colorOptions}
                      disabled={!hasEditPermission}
                      error={errorsColor.heroCardBgColorName?.message}
                      name="heroCardBgColorName"
                      required
                    />
                  </div>
                </FormGroup>
              </div>

              <div className="bg-monochrome-3-60 border border-monochrome-120 p-4 rounded-2xl">
                <FormGroup
                  label="Hero Card Text Color"
                  direction="column"
                  labelWrapperClassName="w-full justify-between"
                  labelClassName="whitespace-nowrap min-w-[230px]"
                >
                  <div className="flex flex-col gap-2 w-full">
                    <SelectField
                      outline
                      placeholder="Select a Color"
                      value={heroCardTextColorNameWatch}
                      onValueChange={(value) =>
                        setColorValue('heroCardTextColorName', value)
                      }
                      options={colorOptions}
                      disabled={!hasEditPermission}
                      error={errorsColor.heroCardTextColorName?.message}
                      name="heroCardTextColorName"
                      required
                    />
                  </div>
                </FormGroup>
              </div>

              <div className="bg-monochrome-3-60 border border-monochrome-120 p-4 rounded-2xl">
                <FormGroup
                  label="Hero Card Error Color"
                  direction="column"
                  labelWrapperClassName="w-full justify-between"
                  labelClassName="whitespace-nowrap min-w-[230px]"
                >
                  <div className="flex flex-col gap-2 w-full">
                    <SelectField
                      outline
                      placeholder="Select a Color"
                      value={heroCardErrorBgColorNameWatch}
                      onValueChange={(value) =>
                        setColorValue('heroCardErrorBgColorName', value)
                      }
                      options={colorOptions}
                      disabled={!hasEditPermission}
                      error={errorsColor.heroCardErrorBgColorName?.message}
                      name="heroCardErrorBgColorName"
                      required
                    />
                  </div>
                </FormGroup>
              </div>
              <div className="bg-monochrome-3-60 border border-monochrome-120 p-4 rounded-2xl">
                <FormGroup
                  label="Hero Card Error Text Color"
                  direction="column"
                  labelWrapperClassName="w-full justify-between"
                  labelClassName="whitespace-nowrap min-w-[230px]"
                >
                  <div className="flex flex-col gap-2 w-full">
                    <SelectField
                      outline
                      placeholder="Select a Color"
                      value={heroCardErrorTextColorNameWatch}
                      onValueChange={(value) =>
                        setColorValue('heroCardErrorTextColorName', value)
                      }
                      options={colorOptions}
                      disabled={!hasEditPermission}
                      error={errorsColor.heroCardErrorTextColorName?.message}
                      name="heroCardErrorTextColorName"
                      required
                    />
                  </div>
                </FormGroup>
              </div>
              <div className="bg-monochrome-3-60 border border-monochrome-120 p-4 rounded-2xl">
                <FormGroup
                  label="Accent/Strong/Link Color"
                  direction="column"
                  labelWrapperClassName="w-full justify-between"
                  labelClassName="whitespace-nowrap min-w-[230px]"
                >
                  <div className="flex flex-col gap-2 w-full">
                    <SelectField
                      outline
                      placeholder="Select a Color"
                      value={textHighlightColorNameWatch}
                      onValueChange={(value) =>
                        setColorValue('textHighlightColorName', value)
                      }
                      options={colorOptions}
                      disabled={!hasEditPermission}
                      error={errorsColor.textHighlightColorName?.message}
                      name="textHighlightColorName"
                      required
                    />
                  </div>
                </FormGroup>
              </div>
              <div className="bg-monochrome-3-60 border border-monochrome-120 p-4 rounded-2xl">
                <FormGroup
                  label="Text Color"
                  direction="column"
                  labelWrapperClassName="w-full justify-between"
                  labelClassName="whitespace-nowrap min-w-[230px]"
                >
                  <div className="flex flex-col gap-2 w-full">
                    <SelectField
                      outline
                      placeholder="Select a Color"
                      value={textColorNameWatch}
                      onValueChange={(value) =>
                        setColorValue('textColorName', value)
                      }
                      options={colorOptions}
                      disabled={!hasEditPermission}
                      error={errorsColor.textColorName?.message}
                      name="textColorName"
                      required
                    />
                  </div>
                </FormGroup>
              </div>
            </div>
          </form>

          <div className="flex justify-end w-full gap-2 border-t border-monochrome-120 p-4 mt-4 [&>*]:w-[154px]">
            <Button
              onClick={handleCancelColor}
              variant="secondary"
              data-dismiss="modal"
              disabled={!hasEditPermission}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              form="settings-email-color-form"
              type="submit"
              disabled={loading || !hasEditPermission}
              loading={loading}
            >
              Save
            </Button>
          </div>
        </div>
        <MessageBox
          message={message}
          setShowMessageBox={setShowMessageBox}
          showMessageBox={showMessageBox}
          isError={isError}
          errorStatus={errorStatus}
        />
      </Card>
    </>
  )
}
